import React, { Fragment, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { Link, useHistory } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import { toasterState } from "Components/common/toaster";
import ContactAvatar from "Pages/Dashboard/contacts/ContactAvatar";
import { AddContactsToList } from "Pages/Dashboard/contacts/AddContactsToList";
import SignalIcon from "./SignalIcon";
import { AccountLogo } from "./AccountLogo";
import { FAMILIES, familyOfType, timeAgo, mainActionOf, habitListId, HIRING, draftStateOf, draftRecipient, savedOrAlwaysOn } from "./signalMeta";
import { openAutomate } from "./SignalAutomateDrawer";
import HiringRow, { FirstCheckRow } from "./HiringRow";
import { addToSequence, addToList, draftEmail, ensureContactLists, listName, reviewDraft, reviewLabel } from "./feedActions";
import DraftLine from "./DraftLine";
import AccountListPickerPopup from "Pages/Dashboard/dataSearchV2/AccountListPicker";
import accountListPickerState from "Pages/Dashboard/dataSearchV2/AccountListPicker/state";
import { AlsoReach } from "./PeopleCard";
import { ReachPeople } from "./ReachPeople";
import { Evidence } from "./custom/CustomEvidence";
import { customEventOf, isCustomType } from "./custom/customSignalMeta";
import "./custom/customSignals.scss";

// CompetitorsPage reads the logo from here.
export { AccountLogo };

export const PAGE = 25;
const FRESH_MS = 10 * 1000;
const POLL_MS = 30 * 1000;
// After a hiring watch starts, its first check lands within minutes: reload the feed every 10 s until the check is
// stamped done on its saved signal (or 3 min pass), so every row it finds shows up, not just the first.
export const FIRST_POLL_MS = 10 * 1000;
const RUN_TEXT = {
  QUEUED: "queued",
  RUNNING: "working on it",
  WAITING_APPROVAL: "waiting for your OK",
  SLEEPING: "waiting",
  SUCCEEDED: "done",
  PARTIAL: "done, some records failed",
  FAILED: "failed",
  CANCELLED: "cancelled"
};

/** The headline with the person or company name in bold (added in front when the headline doesn't carry it). */
// An account's name opens the account, so the row needs no separate Open button.
const Sentence = ({ name, headline, onName }) => {
  const Name = () => (onName ? <button type="button" className="sg-namelink" onClick={onName}>{name}</button> : <b>{name}</b>);
  if (name && headline && headline.includes(name)) {
    const [before, ...after] = headline.split(name);
    return <>{before}<Name />{after.join(name)}</>;
  }
  if (!name) return headline || "Something changed";
  return <><Name />{headline ? ` · ${headline}` : ""}</>;
};

// What the row says once it has been acted on.
const DONE = {
  draft_email: () => "You drafted an email",
  add_to_sequence: name => `You added them to ${name || "a sequence"}`,
  add_to_list: name => `You added them to ${name || "a list"}`,
  find_email: () => "You looked up their email",
  call: () => "You called them",
  open: () => "You opened it"
};

const FeedRow = observer(({ item, onPickList }) => {
  const history = useHistory();
  const [busy, setBusy] = useState(false);
  const e = item.event || {};
  const entity = item.entity || {};
  const isAccount = e.entityType === "ACCOUNT";
  const contactId = !isAccount && entity.contactId;
  const fam = familyOfType(signalsData.catalog, e.signalType);
  const def = signalsData.catalog.find(d => d.id === e.signalType);
  const source = signalsData.saved.find(s => s.signal && s.signal.id === e.savedSignalId);
  const runs = item.agentRuns || [];
  const waiting = runs.find(r => r.status === "WAITING_APPROVAL");
  const unread = !e.read && e.state === "OPEN";
  const actedKind = item.actionType || e.actionType || (e.state === "DISMISSED" ? "dismiss" : null);
  const done = DONE[actedKind] && DONE[actedKind](item.actionTargetName);
  const dismissed = actedKind === "dismiss"; // greyed, nothing to say
  const page = isAccount ? `/accounts/${e.entityId}/overview` : `/contacts/${e.entityId}/activity`;
  // A custom signal: its own name as the label, the quote behind it, and who to reach at the account.
  const custom = isCustomType(e.signalType) ? customEventOf(item) : null;
  const domain = item.entityDomain || entity.domain || item.domain;
  const entityName = custom ? custom.recordName : item.entityName;

  const record = (kind, target) => signalsData.recordAction(e.id, kind, target);
  const markRead = () => unread && signalsData.act(e.id, "read");
  // No record to act on inline (or an account): open its page, where those flows live.
  const openPage = () => {
    markRead();
    history.push(page);
  };
  const recordAndOpen = kind => {
    record(kind);
    history.push(page);
  };
  const busyWhile = async fn => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  // The composer has no "sent" hook, so opening it only marks the row read; it is never "handled" on open or cancel.
  const pick = mainActionOf({ isAccount, email: entity.email, contactId });
  const RUN = {
    open: () => recordAndOpen("open"),
    draft_email: () => busyWhile(async () => (await draftEmail(entity.email, history)) && markRead()),
    // The lookup happens on their page with no result we can observe here, so opening it only marks the row read.
    find_email: openPage,
    page: openPage
  };
  // A suggested email: Review draft opens it in the composer (finding their email first when we only have their
  // LinkedIn page); while it is still being written, Draft email asks for it now and opens it when it lands.
  const draftState = draftStateOf(item);
  const readyDraft = draftState === "ready" ? item.draft : null;
  const recipient = draftRecipient({ email: entity.email, contactId: contactId || null, name: item.entityName }, item.draft);
  const openDraft = draft => busyWhile(async () => (await reviewDraft(draft, recipient, history)) && markRead());
  const askDraft = () => busyWhile(async () => {
    const got = await signalsData.fetchDraft(e.id);
    if (got && got.draftStatus === "ready" && got.draft && (await reviewDraft(got.draft, draftRecipient(recipient, got.draft), history))) markRead();
  });
  const canReachDraft = !!(recipient.email || recipient.contactId || recipient.linkedinUrl);
  // A custom account match with a persona: like a recruiter's hiring post, the people card is who to reach and opens the draft.
  const reachPersona = custom && isAccount && custom.whoToReach && custom.whoToReach.persona ? custom.whoToReach.persona : "";
  // A custom match that already names the person, or a new leader: shown as that person, no lookup.
  const named = custom && isAccount && custom.whoToReach;
  const namedReach = named && named.name && named.linkedinUrl ? { name: named.name, title: named.title, linkedinUrl: named.linkedinUrl } : null;
  const lp = e.signalType === "new_leader" ? e.payload || {} : null;
  const leader = lp && lp.name ? { name: lp.name, title: lp.title, linkedinUrl: lp.linkedinUrl, photoUrl: lp.photoUrl, location: lp.location } : null;
  const main = draftState === "ready" && canReachDraft
    ? { label: reviewLabel(recipient), run: () => openDraft(item.draft) }
    : draftState === "pending"
      ? { label: "Draft email", run: askDraft }
      : { label: pick.label, run: RUN[pick.kind] };
  // A ready draft is sent from inside its preview card, next to the words it sends; the row's corner keeps the quick icons.
  const draftAction = draftState !== "ready" ? null : reachPersona ? (
    <AlsoReach asButton persona={reachPersona} domain={domain} accountId={e.entityId} accountName={entityName} draft={item.draft} onDrafted={markRead} />
  ) : canReachDraft ? (
    <button className="sg-btn sg-btn--small" disabled={busy} onClick={main.run}>{main.label}</button>
  ) : null;
  const draftWho = reachPersona || (namedReach && namedReach.name) || (!isAccount && item.entityName) || "";
  const toSequence = () => (contactId
    ? busyWhile(async () => {
      const picked = await addToSequence(contactId);
      if (picked) await record("add_to_sequence", { targetId: picked.id, targetName: picked.name });
    })
    : openPage());
  // A company goes into an account list, the same picker List Builder uses.
  const toList = () => {
    if (contactId) return onPickList(item);
    if (isAccount && domain) return accountListPickerState.open([{ domain, name: entityName || domain }]);
    return openPage();
  };
  const dismiss = async () => {
    if (await record("dismiss")) toasterState.setToastMsg("Dismissed", "success");
  };

  return (
    <div className={`sg-frow ${unread ? "is-unread" : ""} ${done || dismissed ? "is-handled" : ""}`}>
      <div className="sg-frow__who">
        <span className="sg-frow__av">
          {isAccount ? (
            <AccountLogo domain={domain} name={entityName} />
          ) : (
            <ContactAvatar photo={item.entityPhoto || entity.photoUrl || item.photoUrl} name={item.entityName} />
          )}
          <span className={`sg-frow__badge ${fam.key}`}><SignalIcon id={e.signalType} size={10} strokeWidth={2.2} /></span>
        </span>
        <div className="sg-grow">
          <div className="sg-frow__text"><Sentence name={entityName} headline={custom ? custom.why : e.headline} onName={isAccount ? () => recordAndOpen("open") : null} /></div>
          {custom ? (
            <>
              <Evidence evidence={custom.evidence} max={1} />
              {namedReach ? (
                <ReachPeople label={<>Who to reach: <b>{reachPersona || namedReach.title}</b></>} people={[namedReach]} company={entityName} domain={domain} draft={readyDraft} onDrafted={markRead} />
              ) : reachPersona && (
                <span className="sg-halso">
                  Who to reach: <AlsoReach label={reachPersona} persona={reachPersona} domain={domain} accountId={e.entityId} accountName={entityName} draft={readyDraft} onDrafted={markRead} />
                </span>
              )}
            </>
          ) : (e.detail || item.entitySubtitle) && <div className="sg-frow__why">{e.detail || item.entitySubtitle}</div>}
          {leader && <ReachPeople people={[leader]} company={entityName} domain={domain} />}
          {!done && !dismissed && <DraftLine item={item} who={draftWho} action={draftAction} />}
          {runs.map(r => (
            <div key={r.runId} className="sg-frow__run">
              <SignalIcon id="robot" size={13} /> {r.agentName || "An agent"}: {RUN_TEXT[r.status] || String(r.status || "").toLowerCase()}
            </div>
          ))}
        </div>
      </div>
      <div className="sg-frow__from">
        <span>{(custom && custom.signalName) || (source && source.displayName) || item.savedSignalName || (def && def.label) || "Signal"}</span>
        {runs.length > 0 && <small className="sg-frow__agent"><SignalIcon id="robot" size={12} /> handled by agent</small>}
      </div>
      <div className="sg-frow__when">{timeAgo(e.detectedAt)}</div>
      <div className="sg-frow__acts">
        {dismissed ? null : done ? (
          <span className="sg-frow__done"><SignalIcon id="check" size={13} /> {done}</span>
        ) : (
          <>
            {waiting ? (
              <Link className="sg-btn sg-btn--small sg-btn--ghost" to="/agents/approvals">Review</Link>
            ) : !draftAction && !(isAccount && main.run === RUN.open) && (
              <button className="sg-btn sg-btn--small sg-btn--ghost" disabled={busy} onClick={main.run}>{main.label}</button>
            )}
            {/* A company can't be sequenced; its person is, through Review draft or who to reach. */}
            {!isAccount && (
              <button className="sg-ibtn sg-ibtn--grow" aria-label="Add to a sequence" disabled={busy} onClick={toSequence}><SignalIcon id="send" size={15} /><span className="sg-ibtn__lbl">Add to sequence</span></button>
            )}
            <button className="sg-ibtn sg-ibtn--grow" aria-label={isAccount ? "Add to an account list" : "Add to a list"} disabled={busy} onClick={toList}><SignalIcon id="listAdd" size={15} /><span className="sg-ibtn__lbl">{isAccount ? "Add to account list" : "Add to list"}</span></button>
            {!isAccount && (
              <button className="sg-ibtn sg-ibtn--grow" aria-label="Open to call" disabled={busy} onClick={openPage}><SignalIcon id="phone" size={14} /><span className="sg-ibtn__lbl">Call</span></button>
            )}
            <button className="sg-link sg-link--quiet" disabled={busy} onClick={dismiss}>Dismiss</button>
          </>
        )}
      </div>
    </div>
  );
});

/** The feed could not be loaded: say so and offer a retry, never "all caught up". */
export const FeedError = ({ onRetry }) => (
  <div className="sg-feed__empty" role="alert">
    <b>We couldn't load your signals</b>
    <span>Something went wrong on our side. Your signals are safe.</span>
    <button className="sg-btn sg-btn--small sg-btn--ghost sg-feed__retry" onClick={onRetry}>Retry</button>
  </div>
);

/** "You keep doing the same thing here": offers an agent for it, under the newest entry of that signal type. */
const Nudge = ({ suggestion, savedSignalId, listId }) => {
  const msg = suggestion.message || "";
  const cut = msg.indexOf(". ");
  const first = cut >= 0 ? msg.slice(0, cut + 1) : msg;
  const rest = cut >= 0 ? msg.slice(cut + 2) : "";
  const query = new URLSearchParams({ ...(savedSignalId ? { signalId: savedSignalId } : {}), from: "signals", ...(suggestion.templateId ? { template: suggestion.templateId } : {}), ...(listId ? { listId } : {}) });
  // A tracked signal opens the agent drawer here; anything else still goes to the Agents page.
  const row = savedOrAlwaysOn(signalsData.saved, signalsData.catalog, savedSignalId);
  return (
    <div className="sg-nudge">
      <span className="ag-orb ag-orb--rest ag-orb--sm" aria-hidden="true"><span className="ag-orb__core" /></span>
      <div className="sg-nudge__text"><b>{first}</b> {rest}</div>
      <div className="sg-nudge__acts">
        {row && !row.alwaysOn ? <button className="sg-btn sg-btn--small" onClick={() => openAutomate(row, listId)}>Let an agent do it</button>
          : <Link className="sg-btn sg-btn--small" to={`/agents/new/signal?${query}`}>Let an agent do it</Link>}
        <button className="sg-btn sg-btn--small sg-btn--ghost" onClick={() => signalsData.dismissSuggestion(suggestion.signalType)}>Not now</button>
      </div>
    </div>
  );
};

/** The live feed: newest first, filterable by family, with a nudge when newer entries arrive. */
/**
 * `entityId` + `embedded`: the same rows for one account or contact, inside its page (no title bar, rows stacked to
 * fit a column); `view` "all" there, so what was handled still shows.
 */
const SignalsFeed = ({ savedSignalId = "", onOpenSettings, entityId = "", embedded = false, view = "all", emptyText = "" }) => {
  const [family, setFamily] = useState("all");
  const [, tick] = useState(0);

  // The one shared list picker: the row whose contact it adds, and a counter that opens it.
  const [listFor, setListFor] = useState(null);
  const [listTrigger, setListTrigger] = useState(0);
  const listContactId = ((listFor && listFor.entity) || {}).contactId;
  const listContactIds = useMemo(() => (listContactId ? [listContactId] : []), [listContactId]);
  const pickList = async item => {
    await ensureContactLists();
    setListFor(item);
    setListTrigger(n => n + 1);
  };
  const addPickedList = async (type, listId, close = () => {}) => {
    const id = ((listFor && listFor.event) || {}).id;
    if (listId && listContactId && (await addToList(listContactId, listId))) {
      await signalsData.recordAction(id, "add_to_list", { targetId: listId, targetName: listName(listId) });
    }
    close();
  };

  const load = (page = 0, opts = {}) => signalsData.fetchFeed({ view, savedSignalId: savedSignalId || null, entityId: entityId || null, page, size: PAGE, ...opts });
  useEffect(() => {
    load(0, { reuseWithinMs: FRESH_MS }); // the page may have just loaded this exact list alongside /saved
    if (embedded) {
      // the rows name their saved signal, or its type once it was stopped; only the Signals page loads these itself
      signalsData.fetchSaved();
      if (!signalsData.catalog.length) signalsData.fetchCatalog();
    }
    signalsData.fetchSuggestions();
    signalsData.fetchUnreadCount();
    const timer = setInterval(() => {
      signalsData.checkNewer({ savedSignalId: savedSignalId || null, entityId: entityId || null });
      tick(n => n + 1);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [savedSignalId, entityId, view]); // eslint-disable-line

  // An account's or contact's page keeps its own rows; the Signals page uses the main feed.
  const feed = signalsData.feedFor(entityId || null);
  const items = feed.items;
  // A hiring watch's first check (or a "Check now"): a shimmer row on top and a 10 s reload for the whole check, so
  // rows land as it finds them. Saved first, then the feed: once the stamp shows, that same tick's feed has every row.
  const fc = signalsData.firstCheck;
  const fcDone = signalsData.firstCheckDone();
  const waiting = !!fc && !fcDone && (!savedSignalId || savedSignalId === fc.savedSignalId);
  useEffect(() => {
    if (fc && fcDone) signalsData.endFirstCheck();
  }, [fc, fcDone]);
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setInterval(async () => {
      await signalsData.fetchSaved();
      await load(0);
      tick(n => n + 1);
    }, FIRST_POLL_MS);
    return () => clearInterval(timer);
  }, [waiting, savedSignalId]); // eslint-disable-line
  const famOf = i => familyOfType(signalsData.catalog, (i.event || {}).signalType).key;
  const present = FAMILIES.filter(f => items.some(i => famOf(i) === f.key));
  const shown = family === "all" ? items : items.filter(i => famOf(i) === family);
  const updated = timeAgo(feed.loadedAt) || "Just now";
  // Each nudge sits under the newest shown entry of its signal type.
  const nudgeUnder = {};
  (signalsData.suggestions || []).forEach(sg => {
    const newest = shown.find(i => (i.event || {}).signalType === sg.signalType);
    if (newest) nudgeUnder[(newest.event || {}).id] = sg;
  });

  return (
    <div className={`sg-feed ${embedded ? "sg-feed--embedded" : ""}`}>
      {!embedded && <div className="sg-feed__head">
        <span className="sg-feed__live"><i className="ag-livedot" /> Updated {updated.toLowerCase()}</span>
        {signalsData.newSinceOpen > 0 && (
          <button className="sg-newpill" onClick={() => load(0)}>
            <SignalIcon id="arrowUp" size={13} /> {signalsData.newSinceOpen} new since you opened
          </button>
        )}
        <span className="sg-grow" />
        {onOpenSettings && (
          <button className="sg-link" onClick={onOpenSettings}><SignalIcon id="sliders" size={13} /> Notifications</button>
        )}
      </div>}

      {feed.error && items.length > 0 && !feed.loading && (
        <div className="sg-feed__stale" role="status">
          Couldn't refresh. <button className="sg-link" onClick={() => load(0)}>Retry</button>
        </div>
      )}

      {present.length > 1 && (
        <div className="sg-fchips">
          <button className={`sg-chip ${family === "all" ? "on" : ""}`} onClick={() => setFamily("all")}>All · {items.length}</button>
          {present.map(f => (
            <button key={f.key} className={`sg-chip ${family === f.key ? "on" : ""}`} onClick={() => setFamily(f.key)}>
              {f.label} · {items.filter(i => famOf(i) === f.key).length}
            </button>
          ))}
        </div>
      )}

      <div className="sg-ftable">
        <div className="sg-frow sg-frow--head">
          <div>Signal</div><div>From</div><div>When</div><div className="sg-frow__acts" />
        </div>
        {waiting && <FirstCheckRow accounts={fc.accounts} />}
        {feed.loading && items.length === 0 && !waiting && <><div className="sg-skel" /><div className="sg-skel" /><div className="sg-skel" /></>}
        {shown.map(item => {
          const ev = item.event || {};
          return (
            <Fragment key={ev.id}>
              {ev.signalType === HIRING ? <HiringRow item={item} onPickList={pickList} /> : <FeedRow item={item} onPickList={pickList} />}
              {nudgeUnder[ev.id] && <Nudge suggestion={nudgeUnder[ev.id]} savedSignalId={ev.savedSignalId} listId={habitListId(items, nudgeUnder[ev.id].signalType)} />}
            </Fragment>
          );
        })}
        {!feed.loading && shown.length === 0 && feed.error && <FeedError onRetry={() => load(0)} />}
        {!feed.loading && shown.length === 0 && !feed.error && !waiting && (
          <div className="sg-feed__empty">
            <b>{emptyText || "You're all caught up"}</b>
            {!emptyText && <span>New moves show up here as soon as a check finds them.</span>}
          </div>
        )}
      </div>
      {items.length < feed.total && (
        <button className="sg-btn sg-btn--ghost sg-feed__more" disabled={feed.loading} onClick={() => load(Math.floor(items.length / PAGE))}>
          Load more
        </button>
      )}
      <AddContactsToList hidden contactIds={listContactIds} trigger={listTrigger} addToListCbk={addPickedList} />
      <AccountListPickerPopup />
    </div>
  );
};

export default observer(SignalsFeed);
